<script lang="ts">
  // Drawing toolbar for the Lyktan hearth-drawing surface (S-04-04 + S-04-05).
  //
  // UI-kontrakt vy 7 lists this as the "Ritverktyg-toolbar":
  //   "Snap-to-road-läge / fri ritning-läge / ångra / gör om".
  // We expose mode toggle + ångra (undo) + avbryt (cancel). Gör-om (redo)
  // is intentionally not part of S-04-04; the story acceptance only
  // requires undo. Redo can be added later without changing this contract.
  //
  // The toolbar is presentational: parent owns the state.

  import type { DrawingMode } from './draw/types';

  interface Props {
    mode: DrawingMode;
    canUndo: boolean;
    isBusy: boolean;
    statusMessage?: string | null;
    errorMessage?: string | null;
    // eslint-disable-next-line no-unused-vars
    onModeChange: (next: DrawingMode) => void;
    onUndo: () => void;
    onCancel: () => void;
  }

  const {
    mode,
    canUndo,
    isBusy,
    statusMessage = null,
    errorMessage = null,
    onModeChange,
    onUndo,
    onCancel,
  }: Props = $props();

  const modes: ReadonlyArray<{ id: DrawingMode; label: string; hint: string }> = [
    {
      id: 'snap-to-road',
      label: 'Snap till väg',
      hint: 'Klick för klick följer GraphHopper närmaste väg.',
    },
    {
      id: 'free',
      label: 'Fri ritning',
      hint: 'Klick för klick utan väg-snap. Används när OSM saknar väg.',
    },
  ];
</script>

<div class="drawing-toolbar" role="toolbar" aria-label="Ritverktyg">
  <div class="mode-group" role="group" aria-label="Ritläge">
    {#each modes as option (option.id)}
      <button
        type="button"
        class="mode-button"
        class:active={mode === option.id}
        aria-pressed={mode === option.id}
        title={option.hint}
        disabled={isBusy}
        onclick={() => onModeChange(option.id)}
      >
        {option.label}
      </button>
    {/each}
  </div>

  <div class="action-group">
    <button
      type="button"
      class="action-button"
      onclick={onUndo}
      disabled={!canUndo || isBusy}
      aria-label="Ångra senaste punkt"
    >
      Ångra
    </button>
    <button
      type="button"
      class="action-button"
      onclick={onCancel}
      disabled={!canUndo && !isBusy}
      aria-label="Avbryt ritning"
    >
      Avbryt
    </button>
  </div>

  {#if isBusy}
    <p class="status-line" aria-live="polite">Beräknar väg…</p>
  {:else if errorMessage}
    <p class="status-line error" role="alert">{errorMessage}</p>
  {:else if statusMessage}
    <p class="status-line" aria-live="polite">{statusMessage}</p>
  {/if}
</div>

<style>
  .drawing-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
    padding: 0.6rem 0.85rem;
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text-dim);
  }
  .mode-group,
  .action-group {
    display: inline-flex;
    border-radius: 4px;
    overflow: hidden;
    background: var(--bg);
    border: 1px solid var(--rule);
  }
  .mode-button,
  .action-button {
    appearance: none;
    border: none;
    background: transparent;
    color: inherit;
    padding: 0.45rem 0.85rem;
    font: inherit;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background-color 120ms ease;
  }
  .mode-button:hover:not(:disabled),
  .action-button:hover:not(:disabled) {
    background: var(--accent-soft);
    color: var(--text);
  }
  .mode-button.active {
    background: var(--accent-soft);
    color: var(--glow);
  }
  .mode-button:focus-visible,
  .action-button:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }
  .mode-button:disabled,
  .action-button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  .status-line {
    margin: 0;
    font-size: 0.85rem;
    color: var(--text-dim);
    flex-basis: 100%;
  }
  .status-line.error {
    color: var(--danger);
  }
</style>
